import React, { Fragment, MutableRefObject, useCallback, useEffect, useState } from "react";
import { Column, TableDataInterface } from "./table.data.interface";
import { getColumnIcon } from "../../utils/columnIcons";
import ColumnValueRenderer from "./ColumnValueRenderer";
import useItemViewDiscrepancy from "../../hooks/api/useItemViewDiscrepancy";
import ItemViewDiscrepancyIcon from "./ItemViewDiscrepancy/ItemViewDiscrepancyIcon";
import ItemViewResyncButton from "./ItemViewDiscrepancy/ItemViewResyncButton";
import ResynchronizeAllColumnsModal from "./ItemViewDiscrepancy/ResynchronizeAllColumnsModal";
import { Skeleton, Tooltip } from "@vibe/core";
import { Info, Share } from "@vibe/icons";
import { isSimbManagedColumnTitle } from "../../utils/simbManagedColumns";
import useUser from "../../hooks/context/userHook";
import useForcePushTracker, { RegisterForcePush } from "./ItemViewDiscrepancy/useForcePushTracker";
import ShareColumnButton from "./ShareColumn/ShareColumnButton";
import EditableColumnValue from "./editing/EditableColumnValue";
import { isEmptyColumnValue } from "./editing/isEmptyColumnValue";
import { toSharedColumnPatch } from "./editing/toSharedColumnPatch";
import type { ColumnValueType } from "../../services/monday/monday-query-interface";
import useBoardAndItems from "../../hooks/context/useBoardAndItems";

export const SHARED_COLUMNS_ID = "item-view-shared-columns";

interface itemViewColValProps {
  tableData: TableDataInterface | null;
  isLoading: boolean;
  currentItemIds: number[];
  handleMutateItemLocations: (columnType?: string, columnTitle?: string, latestValue?: any) => void;
  handleRevalidateItemLocations: () => Promise<void>;
  handleHasDiscrepancies: (value: boolean) => void;
  showResynchronizeModal: boolean;
  handleResynchronizeAllColumnsModal: (value: boolean) => void;
  /** Refetches after an edit, keeping the view (not the error state) if that fails. */
  handleRefreshAfterEdit: () => Promise<unknown>;
  /** Replaces a shared column's fields in the view without refetching. */
  handlePatchSharedColumn: (
    columnType: string,
    columnTitle: string,
    patch: Partial<Column>,
  ) => void;
  /** Lets a sibling (the share-column flow) hand its queued value copy to this view's tracker. */
  registerForcePushRef?: MutableRefObject<RegisterForcePush | null>;
}

const ItemViewColumnValues = ({
  tableData,
  isLoading,
  currentItemIds,
  handleMutateItemLocations,
  handleRevalidateItemLocations,
  handlePatchSharedColumn,
  handleRefreshAfterEdit,
  handleHasDiscrepancies,
  showResynchronizeModal,
  handleResynchronizeAllColumnsModal,
  registerForcePushRef,
}: itemViewColValProps) => {
  const handleMutateItemDiscrepancy = (
    columnType?: string,
    columnTitle?: string,
    latestValue?: any,
  ) => {
    const isMultiColumnUpdate = columnType === undefined && columnTitle === undefined;

    if (isMultiColumnUpdate) {
      const updates = Object.keys(latestValue ?? {}).reduce((acc, key) => {
        acc[key] = {
          ...itemViewDiscrepancy?.[key],
          hasDiscrepancy: false,
        };
        return acc;
      }, {} as any);

      mutateItemViewDiscrepancy(
        {
          ...itemViewDiscrepancy,
          ...updates,
        },
        { revalidate: false },
      );
      return;
    }

    const discrepancyKey = `${columnType}_${columnTitle}`;
    mutateItemViewDiscrepancy(
      {
        ...itemViewDiscrepancy,
        [discrepancyKey]: {
          ...itemViewDiscrepancy?.[discrepancyKey],
          hasDiscrepancy: false,
        },
      },
      { revalidate: false },
    );
  };

  const {
    data: itemViewDiscrepancy,
    isLoading: isItemViewDiscrepancyLoading,
    error: itemViewDiscrepancyError,
    mutate: mutateItemViewDiscrepancy,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isValidating: isValidatingItemViewDiscrepancy,
  } = useItemViewDiscrepancy(currentItemIds[0].toString(), tableData?.linkedBoards);

  // Reconciles optimistic UI with server truth after a resync completes. On
  // success this confirms the new state (and forces FilesColumn's Monday-SDK
  // re-fetch via the new `columnData.value` reference); on failure it reverts
  // any optimistic discrepancy clear so the badge reappears for retry.
  const handleRevalidateItemView = async () => {
    await Promise.all([handleRevalidateItemLocations(), mutateItemViewDiscrepancy()]);
  };

  // Tracks slow (file/doc) column keys whose resync API call is currently in
  // flight. Used to render an in-progress spinner on the discrepancy icon so
  // the user sees something is happening for columns whose value display
  // can't be updated optimistically.
  const [resyncingColumnKeys, setResyncingColumnKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const setColumnsResyncing = useCallback((columnKeys: string[], isResyncing: boolean) => {
    if (columnKeys.length === 0) return;
    setResyncingColumnKeys((prev) => {
      const next = new Set(prev);
      columnKeys.forEach((key) => (isResyncing ? next.add(key) : next.delete(key)));
      return next;
    });
  }, []);

  // Resyncs the backend queued through orchestration (202) finish asynchronously; this keeps
  // their spinners on and reconciles the view when their socket completions arrive.
  const { userId, accountId } = useUser();
  const { registerForcePush } = useForcePushTracker({
    userId: userId ?? undefined,
    accountId: accountId ?? undefined,
    setColumnsResyncing,
    revalidate: handleRevalidateItemView,
  });

  if (registerForcePushRef) registerForcePushRef.current = registerForcePush;

  const linkedBoardCount = tableData?.linkedBoards?.length ?? 0;

  // Shared rows hold the current item's own columns, so edits write that item and SIMB's sync
  // copies them on. The other boards haven't caught up yet when the view refetches, so the
  // column's discrepancy badge is cleared rather than refetched.
  const { currentBoard } = useBoardAndItems();
  const handleValueSaved = async (
    columnType: string,
    columnTitle: string,
    saved: boolean,
    written?: ColumnValueType,
    settings?: any,
  ) => {
    if (!saved || !written) {
      await handleRefreshAfterEdit();
      return;
    }
    handleMutateItemDiscrepancy(columnType, columnTitle);
    handlePatchSharedColumn(
      columnType,
      columnTitle,
      toSharedColumnPatch(columnType, written, settings),
    );
    void handleRefreshAfterEdit();
  };

  useEffect(() => {
    if (itemViewDiscrepancy) {
      handleHasDiscrepancies(
        Object.values(itemViewDiscrepancy).some((item: any) => item.hasDiscrepancy),
      );
    }
  }, [itemViewDiscrepancy, handleHasDiscrepancies]);

  return (
    <div id={SHARED_COLUMNS_ID} className="max-w-[640px] mx-auto pt-4 ">
      <div className="flex gap-2 items-center mb-3">
        <h3 className="!font-figtree ">Shared Columns</h3>
        <Tooltip content="Click a value to edit it. Changes sync to every board where this item appears.">
          <div className="flex justify-center items-center cursor-help">
            <Info size={16} color="#676879" />
          </div>
        </Tooltip>
      </div>
      <div className="grid grid-cols-item-view-values-cols gap-x-[2px]">
        {/* Column Values */}
        {isLoading
          ? // Skeleton for column values
            Array.from({ length: 5 }).map((_, index) => (
              <Fragment key={index + 1}>
                <div className="grid grid-cols-[auto_1fr] items-center gap-[5px] min-h-[36px] p-2 ">
                  <span className="flex items-center">
                    <Skeleton type={Skeleton.types.CIRCLE} width={16} height={16} />
                  </span>
                  <div className="flex gap-[5px] items-center">
                    <Skeleton type={Skeleton.types.TEXT} size={Skeleton.sizes.TEXT.P} height={24} />
                  </div>
                </div>
                <div className="flex items-center">
                  <Skeleton wrapperClassName="!w-full" className="!w-full" height={24} />
                </div>
              </Fragment>
            ))
          : tableData &&
            tableData?.columns?.map((column, index) =>
              column.boards.length > 1 && !isSimbManagedColumnTitle(column.title) ? (
                <Fragment key={index + 1}>
                  <div className="flex items-center">
                    {/* Item View Discrepancy Icon */}
                    <ItemViewDiscrepancyIcon
                      itemViewDiscrepancy={itemViewDiscrepancy?.[`${column.type}_${column.title}`]}
                      isItemViewDiscrepancyLoading={isItemViewDiscrepancyLoading}
                      column={column}
                      itemViewDiscrepancyError={itemViewDiscrepancyError}
                      isResyncing={resyncingColumnKeys.has(`${column.type}_${column.title}`)}
                    />

                    <div className="grid grid-cols-[auto_1fr] items-center gap-[5px] min-h-[36px] p-2 ">
                      {/* Column Header */}

                      <span className="flex items-center">{getColumnIcon(column.type)}</span>
                      <div className="flex gap-[5px] items-center">
                        <Tooltip content={column.title}>
                          <p className="!font-figtree text-sm text-light-secondary-text-color overflow-hidden whitespace-nowrap !text-ellipsis max-w-[15ch]">
                            {column.title}
                          </p>
                        </Tooltip>
                        {column.boards.length > 1 && (
                          <Tooltip
                            title="Shared in other boards"
                            content={
                              <div className="grid grid-cols-2 gap-x-[10px] gap-y-[5px] mt-1">
                                {column.boards
                                  ?.filter((board: any) => board?.boardName) // Filter out boards with undefined or null boardName
                                  .slice() // Create a shallow copy to avoid mutating the original array
                                  .sort((a: any, b: any) => a.boardName.localeCompare(b.boardName)) // Sort alphabetically by boardName
                                  .map((board: any, index) => (
                                    <div
                                      key={index + 1} // Ensure each element has a unique key, assuming each board has a unique id
                                      className="text-xs max-w-[150px] text-white !font-figtree font-semibold py-1 px-3 flex items-center justify-center bg-[#EFF6FD26] rounded overflow-hidden"
                                    >
                                      <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                                        {board.boardName}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            }
                          >
                            <span className="flex items-center opacity-50 transition-opacity hover:opacity-100">
                              <Share size={16} color="#676879" />
                            </span>
                          </Tooltip>
                        )}
                        {/* Shared, but not with every board this item lives on */}
                        {column.boards.length < linkedBoardCount && (
                          <ShareColumnButton
                            title={column.title}
                            type={column.type}
                            tooltip="Add to more boards"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Display Column Value */}
                  <div className="flex items-center !font-figtree text-sm space-x-4">
                    {currentBoard ? (
                      <EditableColumnValue
                        columnType={column.type}
                        columnId={column.columnId}
                        boardId={currentBoard}
                        itemId={currentItemIds[0]}
                        onSaved={(saved, written, settings) =>
                          handleValueSaved(column.type, column.title, saved, written, settings)
                        }
                        isEmpty={column.type !== "file" && isEmptyColumnValue(column.value)}
                      >
                        <ColumnValueRenderer columnData={column} />
                      </EditableColumnValue>
                    ) : (
                      <ColumnValueRenderer columnData={column} />
                    )}
                    {/* Item View Resync Button */}
                    {itemViewDiscrepancy?.[`${column.type}_${column.title}`] &&
                      itemViewDiscrepancy?.[`${column.type}_${column.title}`].hasDiscrepancy && (
                        <ItemViewResyncButton
                          values={itemViewDiscrepancy?.[`${column.type}_${column.title}`].values}
                          columnType={column.type}
                          columnTitle={column.title}
                          handleMutateItemLocations={handleMutateItemLocations}
                          handleMutateItemDiscrepancy={handleMutateItemDiscrepancy}
                          handleRevalidateItemView={handleRevalidateItemView}
                          setColumnsResyncing={setColumnsResyncing}
                          registerForcePush={registerForcePush}
                        />
                      )}
                  </div>
                </Fragment>
              ) : null,
            )}
      </div>

      {/* pass only columns that have discrepancies */}
      {showResynchronizeModal && (
        <ResynchronizeAllColumnsModal
          itemId={currentItemIds[0]}
          showModal={showResynchronizeModal}
          setShowModal={handleResynchronizeAllColumnsModal}
          columns={Object.fromEntries(
            Object.entries(itemViewDiscrepancy).filter(
              ([key, column]: [string, any]) => column.hasDiscrepancy,
            ),
          )}
          linkedItems={tableData?.linkedBoards}
          handleMutateItemLocations={handleMutateItemLocations}
          handleMutateItemDiscrepancy={handleMutateItemDiscrepancy}
          handleRevalidateItemView={handleRevalidateItemView}
          setColumnsResyncing={setColumnsResyncing}
          registerForcePush={registerForcePush}
        />
      )}
    </div>
  );
};

export default ItemViewColumnValues;
